import { ChevronLeft } from '@mui/icons-material';
import {
  Box,
  CSSObject,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
  Theme,
  Toolbar,
  Typography
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useIsBackgroundRefetching } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import Loader from 'v2/components/Loader/Loader';
import useNavigationItems from 'v2/hooks/useNavigationItems/useNavigationItems';

const SIDEBAR_WIDTH = 240;
const REFRESH_INDICATOR_LOADER_SIZE = 1;
const REFRESH_INDICATOR_ICON_SIZE = 24;

const openedMixin = (theme: Theme): CSSObject => ({
  overflow: 'visible',
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.enteringScreen,
    easing: theme.transitions.easing.sharp
  }),
  width: SIDEBAR_WIDTH
});

const closedMixin = (theme: Theme): CSSObject => ({
  overflow: 'visible',
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.leavingScreen,
    easing: theme.transitions.easing.sharp
  }),
  width: `calc(${theme.spacing(11)} + 1px)`
});

const StyledDrawer = styled(Drawer)<{ isWide: boolean }>(({ theme }) => ({
  boxSizing: 'border-box',
  flexShrink: 0,
  position: 'relative',
  whiteSpace: 'nowrap',
  width: SIDEBAR_WIDTH,
  zIndex: theme.zIndex.appBar - 1,
  variants: [
    {
      props: ({ isWide }) => isWide,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme)
      }
    },
    {
      props: ({ isWide }) => !isWide,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme)
      }
    }
  ]
}));

const Sidebar = ({ open, toggleSidebar }: { open: boolean; toggleSidebar: () => void }) => {
  const { translation } = useTranslation();
  const location = useLocation();
  const { getNavigationItems } = useNavigationItems({ translation, location });
  const navigationItems = getNavigationItems();
  const isBackgroundRefetching = useIsBackgroundRefetching();

  const [isWide, setIsWIde] = React.useState(true);
  const handleToggleIsOpen = () => {
    setIsWIde((state) => !state);
  };
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const showWideSidebar = isSmallScreen || isWide;

  return (
    <Box
      sx={{
        position: 'relative'
      }}
    >
      <StyledDrawer
        anchor="left"
        isWide={showWideSidebar}
        open={!isSmallScreen || open}
        variant={isSmallScreen ? 'temporary' : 'persistent'}
        onClose={toggleSidebar}
      >
        <Toolbar />
        <Box
          sx={{
            borderBottom: (theme) => `3px solid ${theme.palette.primary.main} !important`,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden auto',
              paddingTop: '20px'
            }}
          >
            <List>
              {navigationItems.map((item) => (
                <ListItem key={item.location}>
                  <ListItemButton
                    id={`${item.location.replace('/', '')}-link`}
                    component={RouterLink}
                    to={item.location}
                    onClick={toggleSidebar}
                    selected={item.isSelected}
                  >
                    <ListItemIcon>
                      <item.icon sx={(theme) => ({ color: theme.palette.text.secondary })} />
                    </ListItemIcon>
                    <ListItemText>{item.text}</ListItemText>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
          {isBackgroundRefetching && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={(theme) => ({ padding: theme.spacing(2, 3), paddingBottom: theme.spacing(4) })}
            >
              {/* Loader sizes itself in em; this fontSize sets the base unit it scales from */}
              <Box sx={{ fontSize: REFRESH_INDICATOR_ICON_SIZE }}>
                <Loader size={REFRESH_INDICATOR_LOADER_SIZE} />
              </Box>
              {showWideSidebar && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {translation.refreshing_data}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
        <Box
          id="collapse-sidebar-button"
          onClick={handleToggleIsOpen}
          sx={(theme) => ({
            alignContent: 'middle',
            aspectRatio: '1/1',
            backgroundColor: theme.custom.background.level1,
            border: `1px solid ${theme.custom.borderAndDivider.default}`,
            borderRadius: '50%',
            boxSizing: 'border-box',
            cursor: 'pointer',
            display: {
              xs: 'none',
              md: 'block'
            },
            height: '26px',
            lineHeight: '1',
            position: 'absolute',
            right: 0,
            textAlign: 'center',
            top: '111px',
            transform: isWide ? 'translateX(50%) rotate(0)' : 'translateX(50%) rotate(-180deg)',
            transition: 'translate 1.3s ease-in-out',
            width: '26px',
            zIndex: theme.zIndex.appBar + 1
          })}
        >
          <ChevronLeft />
        </Box>
      </StyledDrawer>
    </Box>
  );
};

export default Sidebar;
